import React from 'react';
import { Row, Col, Form } from 'antd';
import { I18n } from '@aws-amplify/core';

import { Label } from '../../../components/Label';
import { FormItem } from '../../../components/FormItem';
import { SelectWithCreation } from '@/components/ui/Inputs';
import { useCreateLine } from '@/hooks/v1/useLineMutations';
import { CreateLineRequest } from '@/components/ui/Inputs/types/request/line';
import type { InputDefaultProps } from '../types';
import type { LineDTO } from '@/components/ui/Inputs/types/response/line';

const { useFormInstance, useWatch } = Form;

export function Line({
	fieldName,
	isLoading = false,
	data = [],
	isError = false
}: Readonly<InputDefaultProps<LineDTO>>): JSX.Element {
	const form = useFormInstance();
	const title = I18n.get('Line');
	const organizationId = useWatch([fieldName, 'organization_id'], form);
	const companyId = useWatch([fieldName, 'company_id'], form);
	const sectorId = useWatch([fieldName, 'sector_id'], form);

	const { mutateAsync: createLine, isLoading: isCreatingLine } = useCreateLine();

	function handleSelectLine(id: string): void {
		form.setFieldValue([fieldName, 'line_id'], id);
		form.setFieldValue([fieldName, 'workstation_id'], undefined);
		form.setFieldValue([fieldName, 'activity_id'], undefined);
	}

	function handleClearLine(): void {
		form.setFieldValue([fieldName, 'line_id'], undefined);
		form.setFieldValue([fieldName, 'workstation_id'], undefined);
		form.setFieldValue([fieldName, 'activity_id'], undefined);
	}

	async function handleCreateLine(name: string): Promise<void> {
		const body: CreateLineRequest = {
			name,
			company_id: companyId,
			sector_id: sectorId as string,
			organization_id: organizationId
		};

		const { data } = await createLine({ body });

		if (data?.id) {
			handleSelectLine(data.id);
		}
	}

	return (
		<Col xs={24}>
			<Row gutter={[0, 10]}>
				<Col span={24}>
					<Label>{title}</Label>
				</Col>
				<Col span={24}>
					<FormItem name={[fieldName, 'line_id']}>
						<SelectWithCreation
							showSearch
							allowClear
							size="large"
							allowCreation
							placeholder={title}
							loading={isLoading}
							selectOptions={data}
							onClear={handleClearLine}
							onChange={handleSelectLine}
							onCreate={handleCreateLine}
							creatingData={isCreatingLine}
							disabled={!sectorId || isLoading || isError}
						/>
					</FormItem>
				</Col>
			</Row>
		</Col>
	);
}
